// Automated-Notification snapshot page — 1:1 clone of the on-screen
// Branch Overview dashboard, rendered in a fixed A4-portrait canvas
// so the html2canvas pipeline (frontend capture) and the legacy
// headless-Chromium pipeline both produce the same PNG.
//
// Iter-310 · Snapshot Layout Refinement (per user brief):
//   1. Hero numbers reduced by 30% (44 → 30 / 36 → 25) to reclaim
//      vertical space.
//   2. Full widget parity with the live dashboard:
//        a. Services Summary
//        b. Packages Summary
//        c. Cancellations & No-shows (NEW)
//        d. Most Performing Care Team (NEW)
//        e. Top Customers (NEW)
//   3. Padding + gaps tightened so all five sections + the hero
//      cards fit on a SINGLE A4 page (794×1123 CSS px) without
//      overflow.
//   4. NO *Engine.jsx file touched — this is a page-file feature.
//      Data is fetched via the existing snapshot-token endpoint;
//      persistence is unchanged.
//   5. Applies globally to every branch — the layout is data-driven,
//      not per-branch.
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
// Iter-312 · BI Isolation — snapshot labels + formatters + cancellation
// display live in ReportingEngine.jsx / NotificationEngine.jsx.
import {
  RANGE_LABELS,
  formatSnapshotMoney,
  deriveCancellationLabel,
  deriveCancellationReason,
} from "@/lib/ReportingEngine";
import { formatSnapshotDateLabel } from "@/lib/NotificationEngine";

/**
 * Print-mode Branch Overview.
 * URL contract: /staff/branch/:bid/print/overview?token=…&range=…&date_from=…&date_to=…
 */
export default function BranchOverviewPrint() {
  const { bid } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const rangeKey = params.get("range") || "today";
  const dateFrom = params.get("date_from") || "";
  const dateTo = params.get("date_to") || "";

  const [data, setData] = useState(null);
  const [branchName, setBranchName] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    // No auth interceptor — snapshot token is the only credential.
    (async () => {
      try {
        const r = await axios.get(
          `${API}/reports/snapshot/branch-overview/${bid}`,
          { params: { token, date_from: dateFrom, date_to: dateTo } },
        );
        setData(r.data);
        setBranchName(r.data?.branch_name || "");
      } catch (e) {
        setErr(e?.response?.data?.detail || e?.message || "Failed to load snapshot");
      }
    })();
  }, [bid, token, dateFrom, dateTo]);

  // Flip a signal on <html> once the data is on screen so headless
  // Chrome's virtual-time budget can drain in "steady state" before
  // the screenshot fires — and so the frontend capture helper's
  // `waitForPrintReady` poll can resolve without racing the fetch.
  useEffect(() => {
    if (data) {
      try { document.documentElement.setAttribute("data-print-ready", "1"); } catch (_e) { /* noop */ }
    }
  }, [data]);

  const money = (v) => formatSnapshotMoney(data?.currency, v);

  // Iter-316 — Canvas widened to a fixed 1200px with DYNAMIC (auto)
  // height so every widget renders in full without cropping. List
  // trimming is relaxed accordingly: cancellations, in particular,
  // are shown UNTRIMMED so the receptionist's full verbatim note is
  // never lost. Other lists cap at a comfortable 20 rows — well
  // above the on-screen dashboard's own limits.
  const services = useMemo(() => (data?.services_summary || []).slice(0, 20), [data]);
  const packages = useMemo(() => (data?.packages_summary || []).slice(0, 20), [data]);
  const cancellations = useMemo(() => (data?.cancellations || []), [data]);
  const careTeam = useMemo(() => (data?.top_care_team || []).slice(0, 20), [data]);
  const topCustomers = useMemo(() => (data?.top_customers || []).slice(0, 20), [data]);

  const rangeLabel = RANGE_LABELS[rangeKey] || RANGE_LABELS.today;
  const windowLabel = dateFrom && dateTo
    ? (dateFrom === dateTo ? dateFrom : `${dateFrom} → ${dateTo}`)
    : "—";
  // Iter-312 · date label formatter delegated to NotificationEngine so
  // the live UI + the emailed banner render byte-identical strings.
  const humanDateLabel = formatSnapshotDateLabel(dateFrom, dateTo);

  return (
    <>
      <style>{`
        html, body, #root {
          margin: 0;
          padding: 0;
          background: #ffffff;
          color: #1f2937;
          font-family: 'Manrope', 'Helvetica Neue', Arial, sans-serif;
          -webkit-font-smoothing: antialiased;
        }
        .a4 {
          /* Iter-316 · Snapshot canvas widened to a FIXED 1200px with
             DYNAMIC height (min-height auto). The dashboard container
             is fully rendered and expanded before capture so nothing
             is cropped — cards, cancellation notes, revenue numbers
             all stay whole. */
          width: 1200px;
          min-height: auto;
          margin: 0 auto;
          padding: 28px 36px 24px;
          box-sizing: border-box;
          background: #ffffff;
        }
        .card {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 10px 14px;
        }
        .stat-label {
          text-transform: uppercase;
          letter-spacing: 0.18em;
          font-size: 9px;
          color: #6b7280;
          margin-bottom: 4px;
        }
        /* Iter-310 · Hero numbers reduced 30% (44 → 30 / 36 → 25). */
        .hero-number {
          font-size: 30px;
          font-weight: 700;
          color: #0284c7;   /* sky-600 – "Large Blue Revenue numbers" */
          line-height: 1;
          letter-spacing: -0.02em;
        }
        .hero-number.small { font-size: 25px; }
        .row {
          display: flex;
          gap: 10px;
          margin-top: 8px;
        }
        .row > .card { flex: 1 1 0; min-width: 0; }
        .row.equal > .card { flex: 1 1 0; }
        .list-title {
          text-transform: uppercase;
          letter-spacing: 0.18em;
          font-size: 9px;
          color: #6b7280;
        }
        .list-caption {
          font-size: 9px;
          color: #9ca3af;
        }
        .list { list-style: none; padding: 0; margin: 0; }
        .list li {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 4px 0;
          border-bottom: 1px solid #f3f4f6;
          font-size: 10.5px;
          line-height: 1.3;
        }
        .list li:last-child { border-bottom: 0; }
        .list li .name {
          flex: 1;
          font-weight: 500;
          text-transform: uppercase;
          font-size: 11px;
          color: #1f2937;
          min-width: 0;
          /* Iter-316 · never truncate inside the snapshot — the whole
             point of the wider canvas is that guest names and service
             titles render in full. */
          overflow: visible;
          text-overflow: clip;
          white-space: normal;
          word-break: break-word;
        }
        .list li .qty {
          color: #6b7280;
          font-size: 9.5px;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .list li .money {
          color: #111827;
          font-variant-numeric: tabular-nums;
          min-width: 80px;
          text-align: right;
          font-size: 10px;
          white-space: nowrap;
        }
        .list li .badge {
          font-size: 9px;
          color: #6b7280;
          padding: 1px 6px;
          border: 1px solid #e5e7eb;
          border-radius: 999px;
          background: #fafafa;
          white-space: nowrap;
        }
        .empty-note {
          font-size: 10px;
          color: #9ca3af;
          padding: 8px 0 4px;
        }
      `}</style>

      <div className="a4" data-testid="branch-overview-print-canvas">
        {/* Header: Cocoon logo + branch + date range */}
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingBottom: 10,
            borderBottom: "1px solid #e5e7eb",
            marginBottom: 12,
          }}
          data-testid="print-header"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img
              src="/brand/cocoon-logo.png"
              alt="Cocoon Medical Spa"
              style={{ height: 38, width: "auto" }}
            />
            <div>
              <div
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: 20,
                  lineHeight: 1.05,
                  letterSpacing: "-0.01em",
                }}
              >
                Cocoon Medical Spa
              </div>
              <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>
                Branch Overview snapshot · Automated notification
              </div>
            </div>
          </div>
          <div style={{ textAlign: "right", fontSize: 10, color: "#6b7280" }}>
            <div style={{ fontSize: 13, color: "#111827", fontWeight: 600 }} data-testid="print-branch-name">
              {branchName || "—"}
            </div>
            <div data-testid="print-range-label">{rangeLabel}</div>
            <div data-testid="print-window">{windowLabel}</div>
          </div>
        </header>

        {humanDateLabel && (
          <div
            style={{
              textAlign: "center",
              margin: "-4px 0 10px",
              padding: "6px 12px",
              background: "#f0f9ff",
              border: "1px solid #bae6fd",
              borderRadius: 8,
              color: "#0c4a6e",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.02em",
            }}
            data-testid="print-report-date"
          >
            {humanDateLabel}
          </div>
        )}

        {err && (
          <div
            style={{
              padding: 14,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 10,
              color: "#991b1b",
              fontSize: 12,
            }}
            data-testid="print-error"
          >
            Snapshot data unavailable: {err}
          </div>
        )}

        {!err && !data && (
          <div
            style={{ padding: 30, textAlign: "center", color: "#6b7280", fontSize: 12 }}
            data-testid="print-loading"
          >
            Preparing snapshot…
          </div>
        )}

        {data && (
          <>
            {/* Hero row: Total Revenue + New Guests */}
            <div className="row" data-testid="print-hero-row">
              <div className="card" data-testid="print-total-revenue-card">
                <div className="stat-label">Total Revenue</div>
                <div className="hero-number" data-testid="print-total-revenue">
                  {money(data.total_revenue ?? 0)}
                </div>
                {data.revenue_refunds > 0 && (
                  <div style={{ marginTop: 4, fontSize: 9.5, color: "#6b7280" }}>
                    Gross {money(data.revenue_gross)} · Refunds {money(data.revenue_refunds)}
                  </div>
                )}
                <div style={{ marginTop: 6, fontSize: 9, color: "#9ca3af", lineHeight: 1.3 }}>
                  Net sales for the selected range (payments minus refunds).
                </div>
              </div>
              <div className="card" data-testid="print-new-guests-card">
                <div className="stat-label">New Guests</div>
                <div className="hero-number small" data-testid="print-new-guests">
                  {(data.new_customers ?? 0).toLocaleString("en-US")}
                </div>
                <div style={{ marginTop: 6, fontSize: 9, color: "#9ca3af", lineHeight: 1.3 }}>
                  First-ever visits to this branch in the selected range.
                </div>
              </div>
            </div>

            {/* Secondary row: Guests Treated | Appointments Booked */}
            <div className="row" data-testid="print-secondary-row">
              <div className="card" data-testid="print-guests-treated-card">
                <div className="stat-label">Guests Treated</div>
                <div className="hero-number small" data-testid="print-guests-treated">
                  {(data.guests_treated ?? 0).toLocaleString("en-US")}
                </div>
                <div style={{ marginTop: 6, fontSize: 9, color: "#9ca3af", lineHeight: 1.3 }}>
                  Unique visitors seen in the selected range.
                </div>
              </div>
              <div className="card" data-testid="print-appointments-card">
                <div className="stat-label">Appointments Booked</div>
                <div className="hero-number small" data-testid="print-appointments">
                  {(data.appointments_booked ?? 0).toLocaleString("en-US")}
                </div>
                <div style={{ marginTop: 6, fontSize: 9, color: "#9ca3af", lineHeight: 1.3 }}>
                  Every non-cancelled appointment starting in the selected range.
                </div>
              </div>
            </div>

            {/* Services + Packages (unchanged widgets, tightened rows) */}
            <div className="row" data-testid="print-lists-row">
              <div className="card" data-testid="print-services-card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                  <div className="list-title">Services Summary</div>
                  <div className="list-caption">Qty · Revenue</div>
                </div>
                {services.length === 0 ? (
                  <div className="empty-note" data-testid="print-services-empty">
                    No individual services closed in this range.
                  </div>
                ) : (
                  <ul className="list" data-testid="print-services-list">
                    {services.map((s) => (
                      <li key={s.service_id || s.service_name}>
                        <span className="name">{s.service_name}</span>
                        <span className="qty">×{s.quantity}</span>
                        <span className="money">{money(s.revenue)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="card" data-testid="print-packages-card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                  <div className="list-title">Packages Summary</div>
                  <div className="list-caption">Qty · Revenue</div>
                </div>
                {packages.length === 0 ? (
                  <div className="empty-note" data-testid="print-packages-empty">
                    No packages sold in this range.
                  </div>
                ) : (
                  <ul className="list" data-testid="print-packages-list">
                    {packages.map((p) => (
                      <li key={p.service_id || p.service_name}>
                        <span className="name">{p.service_name}</span>
                        <span className="qty">×{p.quantity}</span>
                        <span className="money">{money(p.revenue)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* NEW · Cancellations + Care Team side-by-side */}
            <div className="row" data-testid="print-cancellations-row">
              <div className="card" data-testid="print-cancellations-card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                  <div className="list-title">Cancellations & No-shows</div>
                  <div className="list-caption">Guest · Reason</div>
                </div>
                {cancellations.length === 0 ? (
                  <div className="empty-note" data-testid="print-cancellations-empty">
                    No cancellations or no-shows in this range.
                  </div>
                ) : (
                  <ul className="list" data-testid="print-cancellations-list">
                    {cancellations.map((c, idx) => {
                      // Iter-312 · label + reason derivation via ReportingEngine.
                      const isPackage = !!c.is_package;
                      const label = deriveCancellationLabel(c);
                      const reasonText = deriveCancellationReason(c);
                      return (
                        <li key={c.package_id || c.customer_id || idx} style={{ alignItems: "flex-start" }}>
                          <span className="name" style={{ whiteSpace: "normal", overflow: "visible", textOverflow: "clip" }}>
                            {c.customer_name || "Walk-in"}
                            {label ? (
                              <span style={{ color: "#6b7280", fontWeight: 400, textTransform: "none" }}>
                                {" — "}
                                {isPackage ? (
                                  <span style={{
                                    background: "#eff6ff", color: "#1d4ed8",
                                    padding: "0 5px", borderRadius: 999,
                                    fontSize: 8.5, fontWeight: 600,
                                    letterSpacing: "0.06em", textTransform: "uppercase",
                                    marginRight: 4,
                                  }}>Package</span>
                                ) : null}
                                {label}
                              </span>
                            ) : null}
                          </span>
                          <span
                            className="badge"
                            style={{
                              // Iter-316 · Verbatim cancellation note
                              // must render in FULL — the widened 1200px
                              // canvas has room, so the badge grows to
                              // fit whatever the receptionist typed
                              // rather than clipping at 200px.
                              whiteSpace: "normal",
                              maxWidth: 560,
                              minWidth: 160,
                              textAlign: "left",
                              fontStyle: "italic",
                              lineHeight: 1.35,
                              wordBreak: "break-word",
                              padding: "4px 10px",
                              fontSize: 11,
                            }}
                            data-testid={`print-cancellation-${idx}-reason`}
                          >
                            {reasonText}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="card" data-testid="print-care-team-card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                  <div className="list-title">Most Performing Care Team</div>
                  <div className="list-caption">Guests · Revenue</div>
                </div>
                {careTeam.length === 0 ? (
                  <div className="empty-note" data-testid="print-care-team-empty">
                    No care-team activity in this range.
                  </div>
                ) : (
                  <ul className="list" data-testid="print-care-team-list">
                    {careTeam.map((ct) => {
                      const guests = ct.unique_customers ?? ct.quantity ?? 0;
                      return (
                        <li key={ct.staff_id || ct.staff_name}>
                          <span className="name">{ct.staff_name || "Unassigned"}</span>
                          <span className="qty">
                            {guests} guest{guests === 1 ? "" : "s"}
                          </span>
                          <span className="money">{money(ct.revenue)}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            {/* NEW · Top Customers full width */}
            <div className="row" data-testid="print-top-customers-row">
              <div className="card" data-testid="print-top-customers-card" style={{ flex: "1 1 100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                  <div className="list-title">Top Customers</div>
                  <div className="list-caption">Visits · Spend</div>
                </div>
                {topCustomers.length === 0 ? (
                  <div className="empty-note" data-testid="print-top-customers-empty">
                    No customer spend in this range.
                  </div>
                ) : (
                  <ul className="list" data-testid="print-top-customers-list">
                    {topCustomers.map((c, idx) => (
                      <li key={c.customer_id || c.customer_name || idx}>
                        <span className="name">{c.customer_name || "Walk-in"}</span>
                        <span className="qty">
                          {(c.visits ?? c.orders ?? 0)} visit{(c.visits ?? c.orders ?? 0) === 1 ? "" : "s"}
                        </span>
                        <span className="money">{money(c.spend)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <footer
              style={{
                marginTop: 12,
                paddingTop: 8,
                borderTop: "1px solid #e5e7eb",
                fontSize: 9,
                color: "#9ca3af",
                textAlign: "center",
              }}
              data-testid="print-footer"
            >
              Cocoon Medical Spa · Automated snapshot generated for {branchName || "the branch"} — {rangeLabel} · {windowLabel}
            </footer>
          </>
        )}
      </div>
    </>
  );
}
